import * as React from "react";
import {
  Box,
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
  Text,
  BoxProps,
  SystemStyleObject,
} from "@chakra-ui/react";
import { FieldError } from "react-hook-form";

import { TranslationKeys } from "utils/localization";
import { useLocalization } from "hooks/useLocalization";
import { resolveChakraSpacing } from "./helpers";

type TextareaProps = ChakraTextareaProps & {
  label?: React.ReactNode;
  error?: FieldError;
  customMessage?: React.ReactNode;
  required?: boolean;
  customError?: TranslationKeys;
  dataTest?: string;
  sx?: SystemStyleObject;
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      customMessage,
      required,
      dataTest,
      customError,
      sx,
      ...props
    },
    ref,
  ) => {
    const { translate } = useLocalization();

    const newProps = resolveChakraSpacing<TextareaProps, TextareaProps>(props);

    const getLabel = () => {
      if (!label) {
        return null;
      }
      if (React.isValidElement(label)) {
        return label;
      }

      return (
        <Text
          variant="form-label"
          color={Boolean(error) ? "ud-error" : "ud-grey-600"}
        >
          {label}
          {required && (
            <Text as="span" pl="3px" color="ud-error">
              *
            </Text>
          )}
        </Text>
      );
    };

    return (
      <Box
        {...(newProps.spacing as BoxProps)}
        pb={error ? "unset" : "16px"}
        textAlign="left"
      >
        {getLabel()}
        <ChakraTextarea
          ref={ref}
          sx={sx}
          borderColor={Boolean(error) ? "ud-error" : "ud-grey-300"}
          {...newProps.rest}
          data-test="global.textarea"
        />

        {error?.message && (
          <Text variant="form-error" pt="4px" pb="16px" m={0}>
            {customError ? translate(customError) : error.message}
          </Text>
        )}
        {customMessage && <Text variant="form-message">{customMessage}</Text>}
      </Box>
    );
  },
);

export default Textarea;
